import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Wordmark } from "../components/Wordmark";
import { ApiError, api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Icon } from "../lib/icons";

// Telegram's login widget calls window.onTelegramAuth(user) on success.
// We forward the payload to the backend and refresh our auth state.
// Set VITE_BOT_USERNAME at build time — the bot's domain must be
// registered with BotFather (/setdomain) before this works.

declare global {
  interface Window {
    onTelegramAuth?: (user: Record<string, unknown>) => void;
  }
}

const BOT_USERNAME = (import.meta.env.VITE_BOT_USERNAME ?? "").trim();

export function Login() {
  const widgetRef = useRef<HTMLDivElement | null>(null);
  const { setAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.onTelegramAuth = async (user) => {
      setError(null);
      try {
        const { id } = await api.loginWithTelegram(user);
        // Seed the auth cache so RequireAuth admits the route on the next
        // render — navigating before the /me refetch would otherwise bounce
        // us straight back to /login.
        setAuthenticated(id);
        navigate("/", { replace: true });
      } catch (err) {
        // Fail loud: surface the reason instead of swallowing it. A 403 means
        // "not the owner"; a 401 means a bad/expired Telegram signature.
        const msg =
          err instanceof ApiError
            ? err.status === 403
              ? "That Telegram account isn't the owner of this inbox."
              : err.status === 401
                ? "Login signature was rejected or expired — try again."
                : `Login failed (${err.status}).`
            : "Login failed — check your connection and try again.";
        setError(msg);
      }
    };

    const host = widgetRef.current;
    if (!host || !BOT_USERNAME) return;
    // Guard against double-injection: React 18 StrictMode (and HMR) run effects
    // twice in dev, and telegram-widget.js injects its button on load — without
    // this guard you get two "Log in with Telegram" buttons. Idempotent: only
    // inject if this host doesn't already carry the widget script.
    if (!host.querySelector('script[src*="telegram-widget"]')) {
      const script = document.createElement("script");
      script.src = "https://telegram.org/js/telegram-widget.js?22";
      script.async = true;
      script.setAttribute("data-telegram-login", BOT_USERNAME);
      script.setAttribute("data-size", "large");
      script.setAttribute("data-radius", "8");
      script.setAttribute("data-onauth", "onTelegramAuth(user)");
      script.setAttribute("data-request-access", "write");
      host.appendChild(script);
    }
    return () => {
      host.replaceChildren();
      delete window.onTelegramAuth;
    };
  }, [setAuthenticated, navigate]);

  return (
    <div className="login">
      <div className="login-inner fade-up">
        <Wordmark size={17} />
        {BOT_USERNAME ? (
          // The Telegram widget renders inside this div. If it hasn't loaded
          // yet, the styled fallback button shows our brand chrome.
          <div ref={widgetRef}>
            <button
              className="login-widget"
              type="button"
              disabled
              aria-label="Loading Telegram widget"
            >
              <Icon.telegram />
              <span>Log in with Telegram</span>
            </button>
          </div>
        ) : (
          <p style={{ color: "var(--text-muted)", fontSize: 13 }}>
            Set <code>VITE_BOT_USERNAME</code> in <code>web/.env.local</code> to enable
            login.
          </p>
        )}
        {error ? (
          <p className="login-error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
      <p className="login-hint">private · single-user</p>
    </div>
  );
}
